import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateMapObjectInput,
  ListMapObjectsResponse,
  MapObjectDTO,
  PatchMapObjectInput,
} from '@warplan/shared';
import { api } from './api-client.js';

export const MAP_OBJECTS_QUERY_KEY = ['map-objects'] as const;

export function useMapObjects() {
  return useQuery({
    queryKey: MAP_OBJECTS_QUERY_KEY,
    queryFn: () => api.get<ListMapObjectsResponse>('/api/objects'),
    staleTime: 60_000,
  });
}

export function useCreateMapObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMapObjectInput) =>
      api.post<MapObjectDTO>('/api/objects', input),
    onSuccess: (created) => {
      qc.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) => {
        if (!prev) return { objects: [created] };
        if (prev.objects.some((o) => o.id === created.id)) return prev;
        return { objects: [...prev.objects, created] };
      });
    },
  });
}

export function usePatchMapObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: PatchMapObjectInput }) =>
      api.patch<MapObjectDTO>(`/api/objects/${id}`, patch),
    onMutate: async ({ id, patch }) => {
      await qc.cancelQueries({ queryKey: MAP_OBJECTS_QUERY_KEY });
      const previous = qc.getQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY);
      qc.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return {
          objects: prev.objects.map((o) => {
            if (o.id !== id) return o;
            const next: MapObjectDTO = {
              ...o,
              ...(patch.kind !== undefined ? { kind: patch.kind } : {}),
              ...(patch.position !== undefined ? { position: patch.position } : {}),
              ...(patch.metadata !== undefined ? { metadata: patch.metadata } : {}),
            };
            return next;
          }),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(MAP_OBJECTS_QUERY_KEY, ctx.previous);
    },
    onSuccess: (updated) => {
      qc.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        return { objects: prev.objects.map((o) => (o.id === updated.id ? updated : o)) };
      });
    },
  });
}

export function useDeleteMapObject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/objects/${id}`),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: MAP_OBJECTS_QUERY_KEY });
      const previous = qc.getQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY);
      qc.setQueryData<ListMapObjectsResponse>(MAP_OBJECTS_QUERY_KEY, (prev) =>
        prev ? { objects: prev.objects.filter((o) => o.id !== id) } : prev,
      );
      return { previous };
    },
    onError: (_err, _id, ctx) => {
      if (ctx?.previous) qc.setQueryData(MAP_OBJECTS_QUERY_KEY, ctx.previous);
    },
  });
}
